import {
  Component,
  Element,
  Event,
  Prop,
  h,
  Host,
  State,
  EventEmitter,
  Fragment,
  Method,
} from "@stencil/core";
import { checkFileSize, checkFileType } from "../../utils/utils";
import { WebComponentError } from "../../interfaces/web-component-error";
import { FileUploaderConfig } from "../../interfaces/file-uploader-config";
import { FileUploaderResult } from "../../interfaces/file-uploader-result";
import { decodeTiff, encodePng } from "image-in-browser";

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

@Component({
  tag: "file-uploader",
  styleUrl: "file-uploader.scss",
  shadow: true,
})
export class FileUploader {
  /**
   * Host element reference
   * @private
   */
  @Element()
  private _elementHost: HTMLElement;

  /**
   * On file change event emitter
   */
  @Event()
  result: EventEmitter<FileUploaderResult | null>;

  /**
   * On error event emitter
   */
  @Event()
  componentError: EventEmitter<WebComponentError>;

  @Prop()
  config: FileUploaderConfig = {
    size: 5000,
    accept: "",
    description: "",
    buttonText: "",
    placeholder: "",
    mode: "default",
  };

  /**
   * Image base64 preview value
   * @private
   */
  @State()
  private _preview: string;

  /**
   * Image base64 preview value
   * @private
   */
  @State()
  private _base64: string;

  /**
   * Video base64 preview value
   * @private
   */
  @State()
  private _type: "video" | "image";

  /**
   * Selected file data
   * @private
   */
  @State()
  private _selectedFile: FileUploaderResult = {
    base64: "",
    filename: "",
    size: 0,
    sizeDescription: "",
  };

  /**
   * Video base64 preview value
   * @private
   */
  @State()
  private documentPreview: boolean = false;

  @State()
  inputFileRef?: HTMLInputElement;

  @Method()
  async selectFileHandler(): Promise<void> {
    const input: HTMLInputElement = this._elementHost.shadowRoot.querySelector("input.fu-file-input");
    input.click();
  }

  @Method()
  async onRemovedSelected(): Promise<void> {
    this._preview = "";
    this.result.emit(null);
  }

  onChangeHandler(files: FileList): boolean {
    if (files.length === 1) {
      const file = files[0];
      if (this.config.size && !checkFileSize(file, this.config.size)) {
        this.componentError.emit({ label: `Maximum file size exceeded. Max file size is: ${this.config.size} Bytes` });
        return false;
      } else if (!checkFileType(file, this.config.accept)) {
        this.componentError.emit({ label: "File type is not allowed" });
        return false;
      }

      this.uploadFile(file);
    } else {
      this.componentError.emit({
        label: files.length === 0 ? "No file uploaded" : "You can only upload one file at the time",
      });
      return false;
    }
  }

  private uploadFile(file: File) {
    const reader = new FileReader();

    reader.onload = async () => {
      let media = false;
      if (checkFileType(file, "image/*")) {
        this._type = "image";
        media = true;
      } else if (checkFileType(file, "video/*")) {
        this._type = "video";
        media = true;
      }
      this._base64 = reader.result as string;
      this._preview = reader.result as string;
      if (this._type === "image" && ["image/tiff", "image/tif"].includes(file.type)) {
        return this.tiffPreview(file);
      }
      this._selectedFile = {
        base64: this._base64,
        filename: file.name,
        size: file.size,
        sizeDescription: this.formatBytes(file.size),
        type: file.type,
      };
      this.result.emit(this._selectedFile);
      this.inputFileRef.value = null;
      this.documentPreview = !media;
    };

    reader.onerror = (err) => {
      this.componentError.emit({ label: "On load file error", details: err });
    };

    reader.readAsDataURL(file);
  }

  private tiffPreview(file: File) {
    const reader = new FileReader();

    reader.onload = async () => {
      const image = decodeTiff({
        data: reader.result as any,
      });

      const imageArray = encodePng({
        image,
      });

      this._preview = await this.blobToBase64(new Blob([imageArray], { type: "image/png" }) as any);

      this._selectedFile = {
        base64: this._base64,
        pngBase64: this._preview,
        filename: file.name,
        size: file.size,
        sizeDescription: this.formatBytes(file.size),
        type: file.type,
      };

      this.result.emit(this._selectedFile);
      this.documentPreview = false;
      this.inputFileRef.value = null;
    };

    reader.onerror = (err) => {
      this.componentError.emit({ label: "On load file error", details: err });
    };

    reader.readAsArrayBuffer(file);
  }

  blobToBase64(blob): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as any);
      reader.readAsDataURL(blob);
    });
  }

  formatBytes(bytes: number, decimals = 2) {
    if (!+bytes) {
      return "0 Bytes";
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  dropFile(e: DragEvent): void {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      this.uploadFile(e.dataTransfer.files[0]);
    }
  }

  defaultTemplate() {
    return <Fragment>
      <div part="drop-area" class={"fu-file-container " + (this._preview ? "selected-file" : "")}
           onDrop={e => this.dropFile(e)}>
        <input
          class="fu-file-input"
          type="file"
          ref={el => (this.inputFileRef = el)}
          onChange={($event: HTMLInputEvent) => this.onChangeHandler($event.target.files)}
          accept={this.config.accept} />
        <span class="fu-file-placeholder">{this.config.placeholder}</span>
        <slot name="placeholder" />
        {this._preview && <Fragment>
          {this._type === "image" &&
            <img src={this._preview}
                 onLoad={() => this.documentPreview = false}
                 onError={() => this.documentPreview = true}
                 alt="image preview" />}
          {this._type === "video" && <video src={this._preview} controls={true} />}
          {this.documentPreview && <div class="fu-document-preview">
            {this._selectedFile.filename} <br /> <small>{this._selectedFile.sizeDescription}</small>
          </div>}
          <button onClick={() => this.onRemovedSelected()} class="fu-file-icon-button">
            <div class="icon-close-button"></div>
          </button>
        </Fragment>}
      </div>
      {
        this.config.description && <p class="fu-file-description">{this.config.description}</p>
      }
      {
        this.config.buttonText &&
        <button class="fu-file-button" onClick={() => this.selectFileHandler()}>{this.config.buttonText}</button>
      }
    </Fragment>;
  }

  buttonTemplate() {
    return <Fragment>
      <input
        class="fu-file-input"
        type="file"
        onChange={($event: HTMLInputEvent) => this.onChangeHandler($event.target.files)}
        accept={this.config.accept} />
      <button class="fu-file-button" onClick={() => this.selectFileHandler()}>{this.config.buttonText}</button>
    </Fragment>;
  }

  hiddenTemplate() {
    return <Fragment>
      <input
        class="fu-file-input"
        type="file"
        onChange={($event: HTMLInputEvent) => this.onChangeHandler($event.target.files)}
        accept={this.config.accept} />
    </Fragment>;
  }

  render() {
    return <Host>
      {this.config.mode === "default" && this.defaultTemplate()}
      {this.config.mode === "button" && this.buttonTemplate()}
      {this.config.mode === "hidden" && this.hiddenTemplate()}
    </Host>;
  }
}
